import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, FileText, Wrench, X, ArrowRight, ArrowLeft, Sparkles, Check, Rocket,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * 3-step cheatsheet shown to first-time users once they land on the
 * dashboard. Opens automatically when `?tour=1` is in the URL (coming
 * from the signup wizard) OR when the authed user has no `onboarded_at`
 * timestamp set yet. Dismissing calls `/auth/me/onboard` which stamps
 * the timestamp so it never re-appears.
 *
 * Each step tells the user where to click next and (optionally) deep-links
 * them to the right page via "Try it now".
 */
const STEPS = [
  {
    key: "lead",
    icon: Users,
    title: "Capture your first lead",
    body: "Head to Leads and tap “New lead”. Record who's asking, what car they drive, and what wrap they want. Leads are triaged in a kanban — slide them along as the conversation progresses.",
    cta: "Open Leads",
    cta_to: "/leads",
    tip: "Tip: leads also flow in automatically from your public lead-form embed.",
  },
  {
    key: "quote",
    icon: FileText,
    title: "Turn it into a quote",
    body: "From the lead card, click “Create quote”. The AI quoting agent pre-fills line items based on the vehicle + wrap scope — review, tweak, and send. Customers get a shareable link with electronic signing.",
    cta: "Open Quotes",
    cta_to: "/quotes",
    tip: "Pro tip: once a quote is accepted, dates and deposit flow straight into a booked job.",
  },
  {
    key: "job",
    icon: Wrench,
    title: "Run the install",
    body: "Jobs stay on the Jobs board so your installers always know what's next. Track per-item stages (A pillars, headlights, mirrors…), log hours, upload before/after photos, and auto-generate an invoice when it ships.",
    cta: "Open Jobs",
    cta_to: "/jobs",
    tip: "Everything you need to run the day is on the Dashboard — jump back here any time.",
  },
];

export default function GuidedTourModal() {
  const { user, refetch } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [dismissing, setDismissing] = useState(false);
  // Session-level flag so the modal doesn't re-mount while refetch resolves.
  const [sessionDismissed, setSessionDismissed] = useState(false);

  // Decide whether to open: ?tour=1 forces open; otherwise auto-open for
  // new users with no onboarded_at. Only runs on dashboard-mounted shells.
  useEffect(() => {
    if (!user) return;
    if (user.is_impersonating) return; // never auto-open while impersonating
    if (sessionDismissed) return;
    const forced = params.get("tour") === "1";
    const neverOnboarded = !user.onboarded_at;
    if (forced || neverOnboarded) setOpen(true);
  }, [user, params, sessionDismissed]);

  const close = async () => {
    setDismissing(true);
    setSessionDismissed(true);
    setOpen(false);
    // Clear ?tour=1 so a refresh doesn't re-open it.
    if (params.get("tour")) {
      params.delete("tour");
      setParams(params, { replace: true });
    }
    // Fire-and-forget the server flag.
    try {
      await api.post("/auth/me/onboard");
      refetch?.();
    } catch {
      /* silent — flag is nice-to-have */
    }
    setDismissing(false);
  };

  if (!open) return null;
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      data-testid="guided-tour-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={() => close(false)}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg rounded-[2rem] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(135deg,#0f172a 0%,#1e293b 60%,#0b1120 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Dismiss */}
        <button
          onClick={() => close(false)}
          data-testid="tour-close-btn"
          className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 z-10"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Hero band */}
        <div className="px-7 pt-7 pb-5" style={{ background: "radial-gradient(circle at top left, rgba(16,185,129,0.14), transparent 60%)" }}>
          <div className="flex items-center gap-2">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500/40 to-blue-500/30 border border-white/10 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">Quick tour</div>
              <div className="font-heading text-base text-white">Get your shop running in 3 steps</div>
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-5">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                <div
                  data-testid={`tour-dot-${s.key}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i < step ? "bg-emerald-400 w-8"
                    : i === step ? "bg-white w-12"
                    : "bg-white/10 w-5"
                  }`}
                />
                {i < STEPS.length - 1 && <span className="w-0.5" />}
              </React.Fragment>
            ))}
            <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-2">
              {step + 1} / {STEPS.length}
            </span>
          </div>
        </div>

        {/* Step body */}
        <div className="px-7 pt-5 pb-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-heading text-2xl text-white tracking-tight">{current.title}</h2>
                  <p className="text-slate-300 text-sm mt-2 leading-relaxed">{current.body}</p>
                  {current.tip && (
                    <div className="mt-3 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/20 px-3 py-2 text-xs text-emerald-100">
                      <Sparkles className="w-3 h-3 inline mr-1" /> {current.tip}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Nav */}
          <div className="mt-7 flex items-center justify-between gap-3">
            {step > 0 ? (
              <button
                onClick={() => setStep(step - 1)}
                data-testid="tour-back-btn"
                className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <button
                onClick={() => close(false)}
                data-testid="tour-skip-btn"
                className="text-xs text-slate-500 hover:text-white transition"
              >
                Skip tour
              </button>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={() => { close(); navigate(current.cta_to); }}
                data-testid={`tour-cta-${current.key}`}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-xs font-semibold transition"
              >
                {current.cta} <ArrowRight className="w-3 h-3" />
              </button>
              {isLast ? (
                <button
                  onClick={() => close(true)}
                  data-testid="tour-done-btn"
                  disabled={dismissing}
                  className="flex items-center gap-2 bg-emerald-400 text-black font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-emerald-300 transition disabled:opacity-60"
                >
                  <Check className="w-4 h-4" /> I'm set
                </button>
              ) : (
                <button
                  onClick={() => setStep(step + 1)}
                  data-testid="tour-next-btn"
                  className="flex items-center gap-2 bg-white text-black font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-slate-100 transition"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
